import React, { useEffect, useState } from 'react';
import { SmallHeader } from '../../../../ui/Header/Header';
import { useSettings } from '../../../../../hooks/useSettings';
import { useTranslations } from '../../../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../../../hooks/Analytics';
import { TrackingInfoEvent } from '../../../../../../../__generated__/loma';
import useOrderTracking from '../../../../../hooks/useOrderTracking';
import { useTheme } from '../../../../../hooks/useTheme';
import TrackingToggleLink from './TrackingToggleLink';
import styles from './TrackingEvents.module.css';
import { useHackStyle } from '../hooks/useHackStyle';
import clsx from 'clsx';

export interface TrackingEventsProps {
  featureName?: string;
}

const TrackingEvents = ({ featureName }: TrackingEventsProps) => {
  const { bodytextDefaultNone, smalltextDefaultNone, smalltextEmphasisNone } =
    useTheme();

  const { cssClass } = useHackStyle();

  const {
    shippingActivityGenericHeaderText,
    shippingActivityLatestUpdateText,
    trackingStatusNoEddSubtext,
  } = useTranslations();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const { eddPanel } = useSettings();

  const { expandEventHistoryByDefault = false } = eddPanel || {};

  const [collapsed, setCollapsed] = useState(!expandEventHistoryByDefault);

  useEffect(() => {
    setCollapsed(!expandEventHistoryByDefault);
  }, [expandEventHistoryByDefault]);

  const logEvent = useAnalyticsPost();

  if (!currentShipment) return null;

  const { history: fullHistory, trackingNumber, carrier } = currentShipment;

  if (!fullHistory || fullHistory.length === 0)
    return (
      <p
        style={{ lineHeight: '1.25rem', marginTop: 0, ...bodytextDefaultNone }}
      >
        {trackingStatusNoEddSubtext}
      </p>
    );

  const history = collapsed ? [fullHistory[0]] : fullHistory;
  const collapsedToggleVisible = fullHistory.length > 1;

  const historyGroupedByDate = history.reduce(
    (
      acc: Map<string, TrackingInfoEvent[]>,
      event: TrackingInfoEvent,
    ): Map<string, TrackingInfoEvent[]> => {
      const {
        dateFormatted: [date],
      } = event;
      const eventList = acc.get(date);
      if (eventList) {
        eventList.push(event);
      } else {
        acc.set(date, [event]);
      }
      return acc;
    },
    new Map<string, TrackingInfoEvent[]>(),
  );

  const handleToggleHistoryClick = () => {
    const clickDescription = collapsed
      ? NarvarClickSubTypes.SHOW_SHIPPING_HISTORY
      : NarvarClickSubTypes.HIDE_SHIPPING_HISTORY;

    logEvent({
      clickCategory: clickDescription,
      clickSubType: clickDescription,
      clickType: NarvarClickTypes.INTERACTION,
      eventCarrierMoniker: carrier,
      eventComponent: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
      eventTrackingNumber: trackingNumber,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
    });

    setCollapsed(!collapsed);
  };

  return (
    <div style={{ margin: '1rem 0' }}>
      {/* TODO: add theme component name for conatiner */}
      <div
        data-test="shippingHistoryCollapseButtonContainer"
        style={{
          marginBottom: collapsed ? '1rem' : '1.25rem',
          width: '100%',
          display: 'flex',
          flexDirection: collapsed ? 'row' : 'column',
          justifyContent: collapsed ? 'space-between' : 'left',
        }}
      >
        <SmallHeader
          as="h3"
          containerCustomStyle={{
            display: 'inline-block',
            marginRight: '0.5rem',
          }}
        >
          {collapsed
            ? shippingActivityLatestUpdateText
            : shippingActivityGenericHeaderText}
        </SmallHeader>
        {collapsedToggleVisible && (
          <TrackingToggleLink
            collapsed={collapsed}
            onClick={handleToggleHistoryClick}
          />
        )}
      </div>
      <ol
        className={styles.orderedList}
        data-test="shippingHistoryEventsContainer"
      >
        {[...historyGroupedByDate.entries()].map(([date, eventList], idx) => (
          <li
            className={styles.orderedlistItem}
            key={date}
            style={{ marginTop: idx && '1.5rem' }}
          >
            <span
              className={clsx({
                [styles.dateExpanded]: !collapsed,
                [styles.dateCollapsed]: collapsed,
              })}
              style={smalltextEmphasisNone}
            >
              <time>{date}</time>
            </span>
            <ol className={styles.orderedList}>
              {eventList.map(
                ({
                  description,
                  formattedDescription,
                  city,
                  state,
                  dateFormatted: [, time],
                }) => {
                  const timeAndLocation = (
                    <>
                      <time>{time}</time>
                      {time && (city || state) && (
                        <span aria-hidden="true">&nbsp;|&nbsp;</span>
                      )}
                      <span>
                        {city}
                        {city && state && ', '}
                        {state}
                      </span>
                    </>
                  );

                  const descriptionElement =
                    formattedDescription &&
                    formattedDescription.format === 'HTML' &&
                    formattedDescription.value ? (
                      <p
                        className={cssClass}
                        style={{ ...bodytextDefaultNone, marginTop: '0' }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: formattedDescription.value,
                        }}
                      />
                    ) : (
                      <p style={{ ...bodytextDefaultNone, marginTop: '0' }}>
                        {description}
                      </p>
                    );

                  return (
                    <li
                      key={`${date}${time}${city ?? ''}${state ?? ''}${
                        description ?? ''
                      }`}
                      className={styles.orderedlistItem}
                    >
                      <span
                        className={clsx({
                          [styles.dateLocationExpanded]: !collapsed,
                          [styles.dateLocationCollapsed]: collapsed,
                        })}
                        style={smalltextDefaultNone}
                      >
                        {timeAndLocation}
                      </span>
                      {descriptionElement}
                    </li>
                  );
                },
              )}
            </ol>
          </li>
        ))}
      </ol>
      {collapsedToggleVisible && (
        <TrackingToggleLink
          collapsed={collapsed}
          onClick={handleToggleHistoryClick}
        />
      )}
    </div>
  );
};

export default TrackingEvents;
